import { Briefcase, Calendar, Clock, Loader2, MapPin, Plus, Truck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { trpc } from '../../lib/trpc';
import type { Job } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

export interface LinkedJobsSectionProps {
  contactId: number;
  isReadOnly?: boolean;
  onAddJob?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  unassigned: {
    label: 'Unassigned',
    className: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  assigned: {
    label: 'Assigned',
    className: 'bg-primary/10 text-primary border-primary/30',
  },
  en_route: {
    label: 'En Route',
    className: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  },
  completed: {
    label: 'Completed',
    className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  canceled: {
    label: 'Canceled',
    className: 'bg-muted text-muted-foreground border-border',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  },
};

export function LinkedJobsSection({
  contactId,
  isReadOnly = false,
  onAddJob,
}: LinkedJobsSectionProps) {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();

  const { data: jobs = [], isLoading } = trpc.jobPlan.jobs.listForContact.useQuery(
    { contactId },
    { enabled: !!contactId }
  );

  const handleCreateJob = () => {
    if (onAddJob) {
      onAddJob();
    } else if (orgId && workspaceId) {
      navigate(`/org/${orgId}/workspace/${workspaceId}/jobs/new?contactId=${contactId}`);
    }
  };

  const handleJobClick = (jobId: number) => {
    if (orgId && workspaceId) {
      navigate(`/org/${orgId}/workspace/${workspaceId}/jobs/${jobId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Scheduled Jobs & History ({jobs.length})
          </span>
        </div>
        {!isReadOnly && (
          <Button
            size="sm"
            onClick={handleCreateJob}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-xs h-7 px-3 gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Schedule Job</span>
          </Button>
        )}
      </div>

      {jobs.length === 0 ? (
        <div className="p-6 rounded-xl border border-dashed border-border bg-slate-50 dark:bg-slate-900/60 text-center space-y-2">
          <Briefcase className="w-8 h-8 text-muted-foreground/60 mx-auto" />
          <p className="text-xs font-medium text-foreground">No jobs scheduled yet</p>
          <p className="text-[11px] text-muted-foreground max-w-xs mx-auto">
            Schedule a service visit, maintenance, or delivery for this customer.
          </p>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCreateJob}
              className="mt-2 text-xs h-7 gap-1"
            >
              <Plus className="w-3 h-3" />
              <span>Schedule First Job</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job: Job) => {
            const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.unassigned;

            return (
              <div
                key={job.id}
                onClick={() => handleJobClick(job.id)}
                className="group p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-xs transition-all cursor-pointer flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs text-foreground group-hover:text-primary transition-colors">
                      {job.title}
                    </span>
                    {job.priority > 0 && (
                      <Badge variant="destructive" className="text-[9px] px-1 py-0 font-mono">
                        P{job.priority}
                      </Badge>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${statusConfig.className}`}
                  >
                    {statusConfig.label}
                  </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-muted-foreground/70" />
                    <span>{job.serviceDate}</span>
                  </div>

                  {job.windowStartOffsetSeconds != null && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-muted-foreground/70" />
                      <span>
                        {formatOffsetTime(job.windowStartOffsetSeconds)}
                        {job.windowEndOffsetSeconds != null
                          ? ` – ${formatOffsetTime(job.windowEndOffsetSeconds)}`
                          : ''}
                      </span>
                    </div>
                  )}

                  {job.assignedOperator && (
                    <div className="flex items-center gap-1 text-primary">
                      <Truck className="w-3 h-3" />
                      <span>{job.assignedOperator.operatorName}</span>
                    </div>
                  )}
                </div>

                {job.formattedAddress && (
                  <div className="flex items-center gap-1 text-[10.5px] text-muted-foreground truncate">
                    <MapPin className="w-3 h-3 shrink-0 opacity-70" />
                    <span className="truncate">{job.formattedAddress}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
